import { Sortable } from "@/lib/dnd/components/Sortable";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateSlide } from "@/store/slideEditorSlice";
import type { Passage, ReadingSlideData } from "@ai-teach/types";
import { Button } from "@ai-teach/ui-web";
import { DEFAULT_VOICE } from "@ai-teach/utils";
import { verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { HiDocumentText, HiPlus } from "react-icons/hi";
import { EditorContainer } from "../../EditorContainer";
import { FluencyQuizSection } from "./FluencyQuizSection";
import { PassageBlock } from "./PassageBlock";
import { ReadingFluencyHeader } from "./ReadingFluencyHeader";

const NO_MISSPELLED_WORDS: string[] = [];

export function ReadingFluencyEditor() {
  const [voiceName, setVoiceName] = useState<string>(DEFAULT_VOICE);
  const [lastAddedId, setLastAddedId] = useState<string | null>(null);
  // Misspelled words live here, not in each block, so the toolbar's Spell Check can fill them all.
  const [misspelledByPassage, setMisspelledByPassage] = useState<Record<string, string[]>>({});
  const dispatch = useAppDispatch();
  const { slide } = useAppSelector((state) => state.slideEditor.present);

  const passages = (slide?.slideData as ReadingSlideData | undefined)?.passages ?? [];

  // biome-ignore lint/correctness/useExhaustiveDependencies: rerun when slide.id changes
  useEffect(() => {
    const voices = passages.map((p) => p.audio?.voice).filter((v): v is string => !!v);

    if (voices.length === 0) {
      setVoiceName(DEFAULT_VOICE);
      return;
    }

    const counts = voices.reduce<Record<string, number>>((acc, v) => {
      acc[v] = (acc[v] ?? 0) + 1;
      return acc;
    }, {});

    const majority = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0];
    setVoiceName(majority ?? DEFAULT_VOICE);
  }, [slide?.id, passages]);

  if (!slide) return null;

  const readingSlideData = slide.slideData as ReadingSlideData;
  const isTextOnly = readingSlideData.isTextOnly;

  const handleAddPassage = () => {
    const newId = crypto.randomUUID();
    setLastAddedId(newId);
    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: {
          ...readingSlideData,
          passages: [
            ...passages,
            {
              id: newId,
              text: "",
              audio: null,
              image: null,
              video: null,
              mediaType: "image",
              spellCheckStatus: "unchecked",
              whitelist: [],
            },
          ],
        },
      })
    );

    toast.success("New passage added!");
  };

  const toggleRecordOnly = () => {
    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: { ...readingSlideData, recordOnly: !readingSlideData.recordOnly },
      })
    );
  };

  const handleReorderPassages = (next: ReadingSlideData["passages"]) => {
    dispatch(
      updateSlide({ slideId: slide.id, slideData: { ...readingSlideData, passages: next } })
    );
  };

  return (
    <>
      <EditorContainer>
        {/* Title bar — slide name (left) + record-only toggle + template badge (right) */}
        <div className="flex items-center justify-between shrink-0 px-3 pt-2 gap-2">
          <h2 className="flex-1 min-w-0 text-sm font-semibold text-primary-900 truncate uppercase tracking-widest">
            {slide.name || "Passage Reading"}
          </h2>
          <div className="flex items-center gap-2 shrink-0">
            <label
              className={`flex items-center gap-1.5 cursor-pointer px-2 py-1 rounded-full border transition-colors ${
                readingSlideData.recordOnly
                  ? "bg-orange-100 border-orange-400 ring-1 ring-orange-300"
                  : "bg-transparent border-orange-200 hover:border-orange-300"
              }`}
            >
              <span
                className={`text-[10px] font-bold uppercase tracking-widest ${
                  readingSlideData.recordOnly ? "text-orange-700" : "text-orange-500"
                }`}
              >
                Record only
              </span>
              <button
                type="button"
                onClick={toggleRecordOnly}
                className={`relative w-8 h-4 flex items-center px-0.5 rounded-full transition-all duration-300 ease-out ring-1 cursor-pointer ${
                  readingSlideData.recordOnly
                    ? "bg-orange-500 ring-orange-600 justify-end"
                    : "bg-orange-200 ring-orange-300"
                }`}
                aria-pressed={!!readingSlideData.recordOnly}
              >
                <span className="w-3 h-3 rounded-full bg-white shadow-sm" />
              </button>
            </label>
            <span className="text-[10px] font-medium text-gray-100 bg-primary-900 px-2 py-0.5 rounded-full shrink-0">
              Passage Reading
            </span>
          </div>
        </div>

        {/* Toolbar — passage layout mode + batch actions (voice · Spell Check · Play All · Generate All) */}
        <ReadingFluencyHeader
          voiceName={voiceName}
          setVoiceName={setVoiceName}
          onMisspelledWords={setMisspelledByPassage}
        />

        {/* Scrollable passages area */}
        <div className="flex-1 overflow-y-auto flex flex-col gap-2 px-2 pb-2 mt-2">
          {passages.length > 0 ? (
            <>
              <div className="flex items-center gap-2 pt-1">
                <span className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">
                  Passages
                </span>
                <span className="text-[10px] text-gray-400">{passages.length}</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
              <Sortable
                items={passages.filter((p): p is Passage & { id: string } => p.id != null)}
                onReorder={handleReorderPassages}
                handle
                ambient
                strategy={verticalListSortingStrategy}
              >
                {(passage, index, dragHandle) => (
                  <PassageBlock
                    passage={passage}
                    index={index}
                    isTextOnly={isTextOnly}
                    voiceName={voiceName}
                    dragHandle={dragHandle}
                    defaultExpanded={passage.id === lastAddedId}
                    misspelledWords={misspelledByPassage[passage.id] ?? NO_MISSPELLED_WORDS}
                    onMisspelledWordsChange={(words) =>
                      setMisspelledByPassage((prev) => ({ ...prev, [passage.id]: words }))
                    }
                  />
                )}
              </Sortable>

              {/* Add Passage sits with the passages, not in a detached footer */}
              <Button
                variant="secondary"
                size="sm"
                onClick={handleAddPassage}
                className="w-full gap-1 text-xs"
              >
                <HiPlus />
                <span>Add Passage</span>
              </Button>

              {/* Post-reading quizzes (fluency only) — authored inside slideData.subQuizzes */}
              <FluencyQuizSection />
            </>
          ) : (
            /* Empty state */
            <button
              type="button"
              onClick={handleAddPassage}
              className="w-full h-full flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50/60 text-gray-400 hover:bg-white hover:border-primary-300 hover:text-primary-500 transition-all duration-150 group"
            >
              <div className="w-10 h-10 rounded-full bg-gray-100 group-hover:bg-primary-50 flex items-center justify-center transition-colors">
                <HiDocumentText className="text-xl text-gray-300 group-hover:text-primary-400 transition-colors" />
              </div>
              <div className="text-center">
                <p className="text-xs font-semibold text-gray-500 group-hover:text-primary-600 transition-colors">
                  No passages yet
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">Click to add your first passage</p>
              </div>
            </button>
          )}
        </div>
      </EditorContainer>
    </>
  );
}
